import { useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'

import { BaseView, BaseText } from '@/components/base'
import { AppButton, CardItem } from '@/components'
import { useAddMoneyStore } from '@/store/useAddMoneyStore'
import { useTheme } from '@/hooks/useTheme'
import useOffsets from '@/hooks/useOffsets'
import { useAppAnalytics } from '@/services/analytics'

export default function AddMoneySuccess() {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const theme = useTheme()
  const offsets = useOffsets()
  const { trackEvent, events } = useAppAnalytics()

  const { selectedCurrency, amount, reset } = useAddMoneyStore()

  // If no transaction data, redirect to dashboard
  useEffect(() => {
    if (!selectedCurrency || !amount || parseFloat(amount) <= 0) {
      navigate('/dashboard', { replace: true })
    } else {
      trackEvent(events.DEPOSIT_COMPLETED, {
        amount: parseFloat(amount),
      })
    }
  }, [selectedCurrency, amount, navigate, trackEvent, events.DEPOSIT_COMPLETED])

  const handleDone = () => {
    // Clear store and navigate to dashboard
    reset()
    navigate('/dashboard', { replace: true })
  }

  if (!selectedCurrency) {
    return null
  }

  return (
    <BaseView
      flex={1}
      bgColor="background.screen"
      className="h-full justify-center items-center px-6"
      style={{
        paddingTop: offsets.top - 30,
        paddingLeft: 13,
        paddingRight: 13,
      }}
    >
      {/* Success Animation/Icon */}
      <BaseView center mb={32}>
        <BaseView
          style={{
            width: 120,
            height: 120,
            borderRadius: 60,
            backgroundColor: theme.colors.success?.default || '#10B981',
            alignItems: 'center',
            justifyContent: 'center',
            marginBottom: 24,
          }}
        >
          <BaseText fontSize={48} style={{ color: 'white' }}>
            ✓
          </BaseText>
        </BaseView>

        <BaseText
          fontSize={28}
          fontFamily="bold"
          color="label.default"
          textAlign="center"
          mb={12}
        >
          Purchase Successful!
        </BaseText>

        <BaseText
          fontSize={16}
          color="label.secondary"
          textAlign="center"
          mb={32}
        >
          Your {selectedCurrency.code} purchase has been initiated
        </BaseText>
      </BaseView>

      {/* Transaction Details Card */}
      <BaseView
        style={{
          backgroundColor: theme.colors.background.cardItemRoot,
          borderRadius: 16,
          padding: 20,
          marginBottom: 32,
          width: '100%',
          maxWidth: 400,
        }}
      >
        {/* Currency Info */}
        <BaseView row center mb={16}>
          <CardItem.CurrencyIcon
            uri={selectedCurrency.icon}
            bgColor={selectedCurrency.avatarBGRefColor || '#ccc'}
            size={40}
            fallbackText={selectedCurrency.code?.substring(0, 2) || '?'}
          />
          <BaseView ml={12} flex={1}>
            <BaseText fontSize={16} fontFamily="semibold" color="label.default">
              {selectedCurrency.name || selectedCurrency.code}
            </BaseText>
            <BaseText fontSize={14} color="label.secondary">
              {selectedCurrency.code}
            </BaseText>
          </BaseView>
        </BaseView>

        {/* Amount */}
        <BaseView row justify="space-between" mb={8}>
          <BaseText fontSize={16} color="label.secondary">
            Amount Purchased
          </BaseText>
          <BaseText fontSize={16} fontFamily="medium" color="label.default">
            ${amount}
          </BaseText>
        </BaseView>

        {/* Status */}
        <BaseView row justify="space-between">
          <BaseText fontSize={16} color="label.secondary">
            Status
          </BaseText>
          <BaseView row center>
            <BaseView
              style={{
                width: 8,
                height: 8,
                borderRadius: 4,
                backgroundColor: theme.colors.warning?.default || '#F59E0B',
                marginRight: 8,
              }}
            />
            <BaseText
              fontSize={16}
              fontFamily="medium"
              style={{ color: theme.colors.warning?.default || '#F59E0B' }}
            >
              Processing
            </BaseText>
          </BaseView>
        </BaseView>
      </BaseView>

      {/* Info Message */}
      <BaseView
        style={{
          backgroundColor: theme.colors.background.cardItemSecondary,
          borderRadius: 12,
          padding: 16,
          marginBottom: 32,
          width: '100%',
          maxWidth: 400,
        }}
      >
        <BaseText fontSize={14} color="label.secondary" textAlign="center">
          Your purchase is being processed. You'll receive a notification once
          the {selectedCurrency.code} is added to your wallet.
        </BaseText>
      </BaseView>

      {/* Done Button */}
      <BaseView style={{ width: '100%', maxWidth: 400 }}>
        <AppButton
          label={t('form:actions.done')}
          onPress={handleDone}
          size="large"
          type="primaryGradient"
        />
      </BaseView>
    </BaseView>
  )
}
